/**
 * Reporting periods: UK tax years, MTD quarterly update periods, date-range
 * presets and time bucketing for charts/reports.
 */
import { addDays, addMonths, type ISODate, makeDate, parts, startOfMonth } from "./dates.ts";

export interface DateRange {
  from: ISODate;
  to: ISODate;
}

// ---------------------------------------------------------------- tax years

/** The calendar year in which the UK tax year containing `date` starts (6 April). */
export function taxYearOf(date: ISODate): number {
  const { y, m, d } = parts(date);
  return m > 4 || (m === 4 && d >= 6) ? y : y - 1;
}

export function taxYearRange(startYear: number): DateRange {
  return { from: makeDate(startYear, 4, 6), to: makeDate(startYear + 1, 4, 5) };
}

/** 2025 → "2025/26" */
export function taxYearLabel(startYear: number): string {
  return `${startYear}/${String((startYear + 1) % 100).padStart(2, "0")}`;
}

/** "2025/26" or "2025-26" → 2025. Returns null when invalid. */
export function parseTaxYearLabel(label: string): number | null {
  const m = /^(\d{4})[/-](\d{2})$/.exec(label.trim());
  if (!m) return null;
  const start = Number(m[1]);
  return (start + 1) % 100 === Number(m[2]) ? start : null;
}

// ------------------------------------------------------ MTD update periods

export type QuarterlyPeriodType = "standard" | "calendar";

export interface UpdatePeriod extends DateRange {
  quarter: 1 | 2 | 3 | 4;
  /** Statutory filing deadline for the quarterly update. */
  deadline: ISODate;
}

/**
 * MTD ITSA quarterly update periods for a tax year.
 * Standard: 6 Apr–5 Jul, 6 Jul–5 Oct, 6 Oct–5 Jan, 6 Jan–5 Apr.
 * Calendar (election): 1 Apr–30 Jun, 1 Jul–30 Sep, 1 Oct–31 Dec, 1 Jan–31 Mar.
 * Deadlines are the 7th of the month after the standard period ends.
 * NOTE: verify against current HMRC guidance before relying on these for filing.
 */
export function mtdUpdatePeriods(startYear: number, type: QuarterlyPeriodType = "standard"): UpdatePeriod[] {
  const y = startYear;
  const deadlines = [makeDate(y, 8, 7), makeDate(y, 11, 7), makeDate(y + 1, 2, 7), makeDate(y + 1, 5, 7)];
  const ranges: DateRange[] =
    type === "standard"
      ? [
          { from: makeDate(y, 4, 6), to: makeDate(y, 7, 5) },
          { from: makeDate(y, 7, 6), to: makeDate(y, 10, 5) },
          { from: makeDate(y, 10, 6), to: makeDate(y + 1, 1, 5) },
          { from: makeDate(y + 1, 1, 6), to: makeDate(y + 1, 4, 5) },
        ]
      : [
          { from: makeDate(y, 4, 1), to: makeDate(y, 6, 30) },
          { from: makeDate(y, 7, 1), to: makeDate(y, 9, 30) },
          { from: makeDate(y, 10, 1), to: makeDate(y, 12, 31) },
          { from: makeDate(y + 1, 1, 1), to: makeDate(y + 1, 3, 31) },
        ];
  return ranges.map((r, i) => ({ ...r, quarter: (i + 1) as 1 | 2 | 3 | 4, deadline: deadlines[i]! }));
}

/** MTD quarterly updates are cumulative: from the start of the first period to the end of this one. */
export function cumulativeRange(periods: UpdatePeriod[], quarter: number): DateRange {
  const first = periods[0]!;
  const current = periods.find((p) => p.quarter === quarter);
  if (!current) throw new Error(`Unknown quarter ${quarter}`);
  return { from: first.from, to: current.to };
}

// ---------------------------------------------------------------- presets

export const RANGE_PRESETS = [
  { value: "this_month", label: "This month" },
  { value: "previous_month", label: "Previous month" },
  { value: "this_year", label: "This year" },
  { value: "previous_year", label: "Previous year" },
  { value: "current_tax_year", label: "Current tax year" },
  { value: "previous_tax_year", label: "Previous tax year" },
  { value: "last_12_months", label: "Last 12 months" },
  { value: "custom", label: "Custom range" },
] as const;

export type RangePreset = (typeof RANGE_PRESETS)[number]["value"];

export function resolvePreset(preset: Exclude<RangePreset, "custom">, todayDate: ISODate): DateRange {
  const { y } = parts(todayDate);
  switch (preset) {
    case "this_month": {
      const from = startOfMonth(todayDate);
      return { from, to: addDays(addMonths(from, 1), -1) };
    }
    case "previous_month": {
      const from = addMonths(startOfMonth(todayDate), -1);
      return { from, to: addDays(startOfMonth(todayDate), -1) };
    }
    case "this_year":
      return { from: makeDate(y, 1, 1), to: makeDate(y, 12, 31) };
    case "previous_year":
      return { from: makeDate(y - 1, 1, 1), to: makeDate(y - 1, 12, 31) };
    case "current_tax_year":
      return taxYearRange(taxYearOf(todayDate));
    case "previous_tax_year":
      return taxYearRange(taxYearOf(todayDate) - 1);
    case "last_12_months":
      return { from: addDays(addMonths(todayDate, -12), 1), to: todayDate };
  }
}

// ---------------------------------------------------------------- buckets

export type Granularity = "month" | "quarter" | "year" | "tax_year";

export interface Bucket extends DateRange {
  key: string;
  label: string;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function bucketStart(date: ISODate, g: Granularity): ISODate {
  const { y, m } = parts(date);
  switch (g) {
    case "month":
      return makeDate(y, m, 1);
    case "quarter":
      return makeDate(y, Math.floor((m - 1) / 3) * 3 + 1, 1);
    case "year":
      return makeDate(y, 1, 1);
    case "tax_year":
      return taxYearRange(taxYearOf(date)).from;
  }
}

function describe(start: ISODate, g: Granularity): Bucket {
  const { y, m } = parts(start);
  switch (g) {
    case "month":
      return {
        key: start.slice(0, 7),
        label: `${MONTHS[m - 1]} ${y}`,
        from: start,
        to: addDays(addMonths(start, 1), -1),
      };
    case "quarter": {
      const q = Math.floor((m - 1) / 3) + 1;
      return { key: `${y}-Q${q}`, label: `Q${q} ${y}`, from: start, to: addDays(addMonths(start, 3), -1) };
    }
    case "year":
      return { key: String(y), label: String(y), from: start, to: makeDate(y, 12, 31) };
    case "tax_year":
      return { key: taxYearLabel(y), label: taxYearLabel(y), ...taxYearRange(y) };
  }
}

export function bucketFor(date: ISODate, g: Granularity): Bucket {
  return describe(bucketStart(date, g), g);
}

/** All buckets that intersect [from, to], in order. */
export function bucketsBetween(range: DateRange, g: Granularity): Bucket[] {
  const out: Bucket[] = [];
  let cursor = bucketStart(range.from, g);
  while (cursor <= range.to) {
    const b = describe(cursor, g);
    out.push(b);
    cursor = addDays(b.to, 1);
  }
  return out;
}
